import { BLANK_PLACEHOLDER } from "config/constant";
import React from "react";
import { StyleSheet, Text, View } from "@react-pdf/renderer";
import type { Style } from "@react-pdf/stylesheet";
import type { TextProps } from "@react-pdf/renderer";

const COLORS = {
  title: "#3A4552",
  text: "#171D23",
  primaryLine: "#4E2D36",
  secondaryLine: "#C6C8CD",
} as const;

const BODY_FONT_SIZE = 9.46;
const BODY_LINE_HEIGHT = 1.5;

export const contractInfoPdfStyles = StyleSheet.create({
  section: {
    display: "flex",
    flexDirection: "column",
    gap: "2.4mm",
  },
  sectionTitle: {
    fontFamily: "Pretendard",
    fontSize: 10,
    lineHeight: 1.7,
    fontWeight: 600,
    color: COLORS.title,
  },
  borderBox: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    borderTop: `0.5mm solid ${COLORS.primaryLine}`,
    borderBottom: `0.5mm solid ${COLORS.primaryLine}`,
  },
  row: {
    width: "100%",
    display: "flex",
    flexDirection: "row",
    gap: "10.2mm",
    paddingTop: "1.3mm",
    paddingBottom: "1.3mm",
  },
  inlineField: {
    flex: 1,
    display: "flex",
    flexDirection: "row",
    alignSelf: "stretch",
    alignItems: "center",
    gap: "4mm",
  },
  labelWrap: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignSelf: "stretch",
  },
  valueWrap: {
    flex: 1,
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
  },
  labelText: {
    fontFamily: "Pretendard",
    fontSize: BODY_FONT_SIZE,
    lineHeight: BODY_LINE_HEIGHT,
    fontWeight: 700,
    color: COLORS.text,
  },
  valueText: {
    fontFamily: "Pretendard",
    fontSize: BODY_FONT_SIZE,
    lineHeight: BODY_LINE_HEIGHT,
    fontWeight: 400,
    color: COLORS.text,
  },
  divider: {
    width: "100%",
    height: "0.2mm",
    backgroundColor: COLORS.secondaryLine,
  },
});

const isBlankValue = (value: unknown) => {
  if (value === undefined || value === null) return true;
  if (typeof value === "string") return value.trim().length === 0;
  return false;
};

export const formatContractInfoValue = (value: unknown) => {
  if (isBlankValue(value)) return BLANK_PLACEHOLDER;

  return String(value);
};

export const formatContractInfoNumber = (value: unknown) => {
  if (isBlankValue(value)) return BLANK_PLACEHOLDER;

  if (typeof value === "number") {
    return value.toLocaleString("ko-KR");
  }

  const normalizedValue = Number(String(value).replace(/,/g, ""));

  if (Number.isNaN(normalizedValue)) {
    return String(value);
  }

  return normalizedValue.toLocaleString("ko-KR");
};

export const getProfitabilityUnitLabel = (unit?: string) => {
  switch (unit) {
    case "1000":
      return "천원";
    case "1000000":
      return "백만원";
    case "1":
    default:
      return "원";
  }
};

export const formatProfitabilityAmount = (value: unknown, unit = "1") => {
  if (isBlankValue(value)) return BLANK_PLACEHOLDER;

  const normalizedValue = Number(String(value).replace(/,/g, ""));
  const unitValue = Number(unit);

  if (
    Number.isNaN(normalizedValue) ||
    Number.isNaN(unitValue) ||
    unitValue <= 0
  ) {
    return String(value);
  }

  const displayValue = normalizedValue / unitValue;

  if (displayValue % 1 === 0) {
    return displayValue.toLocaleString("ko-KR");
  }

  return displayValue.toLocaleString("ko-KR", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });
};

interface ContractInfoSectionProps {
  title: string;
  children: React.ReactNode;
}

export const ContractInfoSection = ({
  title,
  children,
}: ContractInfoSectionProps) => {
  return (
    <View style={contractInfoPdfStyles.section}>
      <Text style={contractInfoPdfStyles.sectionTitle}>{title}</Text>
      {children}
    </View>
  );
};

interface ContractInfoBorderBoxProps {
  children: React.ReactNode;
  style?: Style;
}

export const ContractInfoBorderBox = ({
  children,
  style = {},
}: ContractInfoBorderBoxProps) => {
  return (
    <View style={[contractInfoPdfStyles.borderBox, style]}>{children}</View>
  );
};

interface ContractInfoRowProps {
  children: React.ReactNode;
  borderBottom?: boolean;
  minHeight?: string;
  alignItems?: "center" | "flex-start" | "stretch";
  gap?: string;
  paddingTop?: string;
  paddingBottom?: string;
  style?: Style;
}

export const ContractInfoRow = ({
  children,
  borderBottom = true,
  minHeight = "6mm",
  alignItems = "center",
  gap = "10.2mm",
  paddingTop = "1.3mm",
  paddingBottom = "1.3mm",
  style = {},
}: ContractInfoRowProps) => {
  return (
    <View
      style={[
        contractInfoPdfStyles.row,
        {
          minHeight,
          alignItems,
          gap,
          paddingTop,
          paddingBottom,
          borderBottom: borderBottom
            ? `0.2mm solid ${COLORS.secondaryLine}`
            : undefined,
        },
        style,
      ]}
    >
      {children}
    </View>
  );
};

interface ContractInfoInlineFieldProps {
  label: string;
  children: React.ReactNode;
  labelWidth?: string;
  alignItems?: "center" | "flex-start";
  style?: Style;
  labelStyle?: Style;
}

export const ContractInfoInlineField = ({
  label,
  children,
  labelWidth = "23.5mm",
  alignItems = "center",
  style = {},
  labelStyle = {},
}: ContractInfoInlineFieldProps) => {
  return (
    <View
      style={[
        contractInfoPdfStyles.inlineField,
        {
          alignItems,
        },
        style,
      ]}
    >
      <View style={[contractInfoPdfStyles.labelWrap, { width: labelWidth }]}>
        <Text style={[contractInfoPdfStyles.labelText, labelStyle]}>
          {label}
        </Text>
      </View>
      <View style={contractInfoPdfStyles.valueWrap}>{children}</View>
    </View>
  );
};

interface ContractInfoValueTextProps extends TextProps {
  children: React.ReactNode;
  style?: Style;
}

export const ContractInfoValueText = ({
  children,
  style = {},
  ...rest
}: ContractInfoValueTextProps) => {
  return (
    <Text style={[contractInfoPdfStyles.valueText, style]} {...rest}>
      {children}
    </Text>
  );
};

export const ContractInfoDivider = ({ style = {} }: { style?: Style }) => {
  return <View style={[contractInfoPdfStyles.divider, style]} />;
};
